import { z } from 'zod';
import { isoDateTimeSchema, localDateSchema, uuidSchema } from './common';
import { jobDtoSchema } from './import';

/**
 * Primitives shared by the wave 4 contracts (Phases 7 to 9: skills and training, scorecards,
 * weekly reviews and the cockpit, meetings, appraisal, AI and the variable pay engine; D-800 ff.).
 * One file per area follows the PTSTDM pattern (`skills.ts`, `training.ts`, `scorecards.ts`,
 * `weekly-reviews.ts`, `meetings.ts`, `transcripts.ts`, `appraisal.ts`, `ai.ts`, `compensation.ts`,
 * `pay-plans.ts`, `pay-periods.ts`, `pay-disputes.ts`, `pay-exports.ts`, `member-grants.ts`); the
 * catalogue of every route is `performance-endpoints.ts`.
 *
 * Conventions that hold for all of them (PLAN §4):
 * - request bodies are strict objects; unknown keys answer `400 validation_failed`;
 * - calendar dates are `YYYY-MM-DD` in the workspace timezone, instants ISO-8601 UTC;
 * - quantities that come out of the decimal maths (achievement, targets, rates) are decimal
 *   strings, counts are integers;
 * - **money is whole paise as a decimal string** (D-066: a sealed amount is a decimal string of
 *   paise), written `…Paise`. Responses always send the string; requests also accept an integer
 *   number. Amounts are absent, never null, for a viewer who may not see them (PLAN §4 "Visibility").
 */

// ---------------------------------------------------------------- money

/** Whole paise as a decimal string, signed (holds and clawbacks are negative): at most 15 digits. */
export const PAISE_PATTERN = /^-?\d{1,15}$/;
export const paiseStringSchema = z.string().regex(PAISE_PATTERN, 'Amounts are whole paise');
export type PaiseString = z.infer<typeof paiseStringSchema>;

/** As {@link paiseStringSchema}, and an integer `number` is accepted and turned into the string. */
export const paiseInputSchema = z.union([
  paiseStringSchema,
  z
    .number()
    .int()
    .min(-999_999_999_999_999)
    .max(999_999_999_999_999)
    .transform((value) => String(value)),
]);

/** A non-negative amount of whole paise (salaries, eligible amounts, caps). */
export const nonNegativePaiseInputSchema = paiseInputSchema.refine(
  (value) => !value.startsWith('-'),
  'This amount cannot be negative',
);

// ---------------------------------------------------------------- periods and weeks

/** `YYYY-MM`, a calendar month in the workspace timezone (`/scorecards/me?period=`). */
export const MONTH_PERIOD_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;
export const monthPeriodSchema = z.string().regex(MONTH_PERIOD_PATTERN, 'Use YYYY-MM');

/** True when the `YYYY-MM-DD` calendar date is a Monday (ISO week start). */
export function isMondayDate(date: string): boolean {
  const parsed = new Date(`${date}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.getUTCDay() === 1;
}

/** The Monday a weekly review starts on (`weekStart`). */
export const weekStartSchema = localDateSchema.refine(isMondayDate, 'weekStart must be a Monday');

/** Trend windows of dashboards, weekly reviews and meeting health: four or twelve weeks. */
export const TREND_WEEK_OPTIONS = [4, 12] as const;
export type TrendWeeks = (typeof TREND_WEEK_OPTIONS)[number];
/** Query-string form (`?weeks=12`); defaults to 4. */
export const trendWeeksQuerySchema = z.coerce
  .number()
  .pipe(z.union([z.literal(4), z.literal(12)]))
  .default(4);

// ---------------------------------------------------------------- limits

/** Fixed limits the server enforces for the wave 4 areas; clients use them to warn early. */
export const PERFORMANCE_LIMITS = Object.freeze({
  /** `skills.name`, `training_modules.title`, templates, forms. */
  nameMax: 120,
  descriptionMax: 2_000,
  noteMax: 4_000,
  /** Free-text reasons (waive, override, calibrate, dispute, void). */
  reasonMax: 2_000,
  reasonMin: 3,
  /** `POST /training/assignments`: people per request. */
  assignmentBatchMax: 200,
  /** Quiz size. */
  quizQuestionsMax: 100,
  quizOptionsMax: 10,
  /** Meeting notes: characters of text (PLAN: 50,000). */
  meetingNoteTextMax: 50_000,
  /** Attendees of one series. */
  meetingAttendeesMax: 100,
  /** `POST /ai/drafts/bulk`. */
  aiBulkMax: 50,
  /** Plan components and gates per version, tiers per component. */
  planComponentsMax: 12,
  planGatesMax: 12,
  planTiersMax: 20,
  /** Dispute evidence files. */
  disputeEvidenceMax: 10,
} as const);

// ---------------------------------------------------------------- shared shapes

/**
 * The body of an action endpoint that takes no fields (`POST …/start`, `…/publish`): `{}`, or no
 * body at all (Fastify hands a body-less POST to the validator as `null`, a client that sends
 * nothing is not wrong). A body that is present must be an empty object; unknown keys answer 400.
 */
export const actionBodySchema = z.strictObject({}).nullish();

/** `If-Match` as sent by the web: `"<version>"`, optional (without it the write is last-write-wins). */
export const performanceIfMatchHeaderSchema = z.looseObject({
  'if-match': z
    .string()
    .regex(/^\s*(?:W\/)?"?\d{1,9}"?\s*$/, 'Expected If-Match: "<version>"')
    .optional(),
});

/** A person reference shown on rows: enough to render a name and an avatar. */
export const personRefSchema = z.object({
  userId: uuidSchema,
  fullName: z.string(),
  displayName: z.string().nullable(),
  title: z.string().nullable(),
  departmentId: uuidSchema.nullable(),
});
export type PersonRef = z.infer<typeof personRefSchema>;

/** `202 { job }`: a long-running operation was queued (`GET /jobs/:jobId` gives its status). */
export const acceptedJobResponseSchema = z.object({ job: jobDtoSchema });
export type AcceptedJobResponse = z.infer<typeof acceptedJobResponseSchema>;

/** `{ items, nextCursor: null }` for bounded lists, re-exported name for the wave 4 files. */
export function itemsOf<T extends z.ZodType>(item: T) {
  return z.object({ items: z.array(item), nextCursor: z.null() });
}

/** A frozen "as of" stamp carried by snapshot-style responses. */
export const asOfSchema = z.object({ asOf: isoDateTimeSchema });
